import {
  ContentKind,
  DOCUMENT_EXCLUSIVE_MESSAGE,
  documentTitleFromFileName,
  isDocumentMime,
  postTextOf,
} from '@mg/shared';
import { mediaCompositionOf, mediaRuleViolation, type MediaComposition } from '@mg/shared';
import type { UpdateContentDto } from '@mg/shared';
import type { SocialPlatform } from '@mg/shared';
import type { Content, CreateContentDto } from '@mg/shared';
import { splitTrailingHashtags } from '../social/previewRules';

/**
 * A Content piece in the composer's local editor state. Mirrors a server
 * {@link Content} but adds a stable local `key` (so React lists stay identity-
 * stable across reorders before anything is persisted) and keeps `id` optional
 * (absent until the piece is saved).
 */
export interface EditorBlock {
  /** Stable local key for React (independent of the server id). */
  key: string;
  /** Server Content id — present only once persisted. */
  id?: string;
  kind: ContentKind;
  /** TEXT body; a DOCUMENT's title (LinkedIn shows it above the pages); empty for other media. */
  text: string;
  /** IMAGE/AUDIO/VIDEO/DOCUMENT backing asset. */
  assetId: string | null;
  /** Proxy URL for previewing the asset (mapper-populated / from an upload). */
  assetUrl: string | null;
  assetMimeType: string | null;
  /** A VIDEO's still frame — the only thing a tile can show, since its blob is not an image. */
  assetPosterUrl: string | null;
  /**
   * A small copy for the editor's 112px tiles. Null when the asset has none — an SVG, an externally
   * hosted file, or one the backfill has not reached — in which case callers fall back to
   * `assetUrl`.
   */
  assetThumbUrl?: string | null;
  /** The file's name - a DOCUMENT's tile shows it, and its default title comes from it. */
  assetName?: string | null;
  /** A DOCUMENT's page count, when it could be read at upload. */
  assetPageCount?: number | null;
  /**
   * In a thread: the stable key of the thread post this block belongs to. Absent on every block of
   * any other post.
   */
  threadKey?: string;
  /**
   * From the saved post: this block's thread post is live, or waiting for a decision. It cannot
   * change.
   */
  lock?: 'live' | 'unconfirmed';
}

let seq = 0;
function nextKey(): string {
  seq += 1;
  return `block-${seq}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * A fresh key, for a caller that needs one before the block it names exists: a thread post's
 * `threadKey`, made in the click handler because a state updater runs twice under StrictMode.
 */
export { nextKey as newBlockKey };

export function newTextBlock(text = ''): EditorBlock {
  return {
    key: nextKey(),
    kind: ContentKind.TEXT,
    text,
    assetId: null,
    assetUrl: null,
    assetMimeType: null,
    assetPosterUrl: null,
  };
}

/** A fresh media block (IMAGE/AUDIO/VIDEO) — no text, awaiting an uploaded asset. */
function newMediaBlock(kind: ContentKind): EditorBlock {
  return {
    key: nextKey(),
    kind,
    text: '',
    assetId: null,
    assetUrl: null,
    assetMimeType: null,
    assetPosterUrl: null,
  };
}

export function newImageBlock(): EditorBlock {
  return newMediaBlock(ContentKind.IMAGE);
}

export function newAudioBlock(): EditorBlock {
  return newMediaBlock(ContentKind.AUDIO);
}

export function newVideoBlock(): EditorBlock {
  return newMediaBlock(ContentKind.VIDEO);
}

/** An empty PDF slot - a LinkedIn carousel - awaiting its file. */
export function newDocumentBlock(): EditorBlock {
  return newMediaBlock(ContentKind.DOCUMENT);
}

/** What the post preview draws: the text, the hashtags that end it, and the leading picture. */
export interface PreviewSource {
  text: string;
  hashtags: string[];
  /** For a PDF, its first page. */
  mediaUrl: string | null;
  /** A PDF the post carries - LinkedIn shows it as a carousel under this title. */
  document: PreviewDocument | null;
  /** Nothing to draw yet: no text, no hashtags and no file. */
  empty: boolean;
  /**
   * Every picture of the post, in order - its small copy where there is one, since several are
   * drawn at once, and its original for a picture drawn the whole card wide. None when a PDF or a
   * video is the whole attachment. With two or more the preview draws them instead of `mediaUrl`:
   * as a grid of the small copies, or on Instagram as a carousel of the originals, one at a time.
   */
  images: PreviewImage[];
}

/** One picture of the post, by the key of its block, and where to load it from. */
export interface PreviewImage {
  key: string;
  /** Its small copy, for a grid cell - or the original, where no small copy was made. */
  url: string | null;
  /**
   * The original, for a picture drawn the whole card wide - a slide of Instagram's carousel - as the
   * one picture of a post is (`mediaUrl`): the small copy is 300px wide, and stretched over the
   * card it would be blurred.
   */
  full?: string | null;
}

/** Said where the preview would be, while the post has nothing in it to draw. */
export const EMPTY_PREVIEW_MESSAGE = 'Write your post to see how it will look.';

export interface PreviewDocument {
  title: string;
  pageCount: number | null;
  /** The PDF's asset: paging through the preview fetches the pages after the first from it. */
  assetId: string | null;
}

/**
 * What the post preview shows from a post's blocks, chosen the way the publisher chooses
 * (posts.service.ts `deliverNatively`). The text is the first text piece that has any, trimmed
 * (`postTextOf`), split from the hashtags that end it - and never the post's title: that is
 * internal and is never published, so a post with no text shows none. The picture is the one that
 * leads the post: a video is the whole attachment wherever it sits, so the first video wins over
 * any image and shows its still frame (the only part of a video an <img> can show); otherwise the
 * first image. Only a block with an asset counts - an empty placeholder never hides a filled block
 * after it. Null `mediaUrl` for a text-only post, or a video with no still frame yet.
 */
export function previewFromBlocks(blocks: EditorBlock[]): PreviewSource {
  const { text, hashtags } = splitTrailingHashtags(postTextOf(blocks) ?? '');
  const filled = (kind: ContentKind) =>
    blocks.find((b) => b.kind === kind && (Boolean(b.assetId) || Boolean(b.assetUrl)));
  // A PDF is the whole attachment, like a video: its first page is the picture - never the PDF
  // itself, which an <img> cannot draw - under the title the publisher will send (the author's, else
  // the file's name, else "Document" - never the post's).
  const pdf = filled(ContentKind.DOCUMENT);
  if (pdf) {
    return {
      text,
      hashtags,
      mediaUrl: pdf.assetThumbUrl ?? null,
      document: {
        title: pdf.text.trim() || documentTitleFromFileName(pdf.assetName) || 'Document',
        pageCount: pdf.assetPageCount ?? null,
        assetId: pdf.assetId,
      },
      empty: false,
      images: [],
    };
  }
  const video = filled(ContentKind.VIDEO);
  const image = filled(ContentKind.IMAGE);
  const mediaUrl = video ? video.assetPosterUrl : (image?.assetUrl ?? null);
  return {
    text,
    hashtags,
    mediaUrl,
    document: null,
    empty: !text && hashtags.length === 0 && !video && !image,
    // A video is the whole attachment: the images beside it are not sent, so they are not drawn.
    images: video
      ? []
      : blocks
          .filter((b) => b.kind === ContentKind.IMAGE && (Boolean(b.assetId) || Boolean(b.assetUrl)))
          .map((b) => ({
            key: b.key,
            url: b.assetThumbUrl ?? b.assetUrl,
            full: b.assetUrl ?? null,
          })),
  };
}

/** The two posts {@link choosePreview} chooses between, and what decides. */
interface PreviewChoice<T> {
  locked: boolean;
  /** The post the composer was opened on; none for a new post. */
  openedId: string | null | undefined;
  hydratedId: string | null;
  saved: T | null;
  form: T;
}

/**
 * Which post the preview draws: the `saved` one while the post is locked - it cannot change, and the
 * form may still hold an edit that will never be saved - and while an opened post has not yet been
 * filled into the form (`hydratedId` is the post the form was filled from; until then the form is
 * the composer's empty starting state). A post created in this session was never opened, so it
 * never waits: the form is where it came from. Otherwise the post as the form has it.
 *
 * Generic over WHAT is chosen - a preview source, or a post's blocks and title, from which a thread
 * is told from a single post only after the choice. The first signature is the one this always had,
 * kept so that a caller holding two preview sources of differently narrowed types still compiles.
 */
export function choosePreview(choice: PreviewChoice<PreviewSource>): PreviewSource;
export function choosePreview<T>(choice: PreviewChoice<T>): T;
export function choosePreview<T>({ locked, openedId, hydratedId, saved, form }: PreviewChoice<T>): T {
  const formLoaded = openedId == null || hydratedId === openedId;
  return saved && (locked || !formLoaded) ? saved : form;
}

/** Map a persisted Content into an editor block. */
export function blockFromContent(content: Content): EditorBlock {
  return {
    key: nextKey(),
    id: content.id,
    kind: content.kind,
    text: content.text ?? '',
    assetId: content.assetId,
    assetUrl: content.assetUrl,
    assetMimeType: content.assetMimeType,
    assetPosterUrl: content.assetPosterUrl,
    assetThumbUrl: content.assetThumbUrl,
    assetName: content.assetName,
    assetPageCount: content.assetPageCount,
  };
}

/** Which blocks persist their `text`: a TEXT's body, and a DOCUMENT's title. */
export function savesText(block: EditorBlock): boolean {
  return block.kind === ContentKind.TEXT || block.kind === ContentKind.DOCUMENT;
}

/**
 * Whether a block is saved at all. An IMAGE piece with no asset is a placeholder the gallery renders
 * as its dropzone, not content - persisting it would leave an invisible, unremovable row on the post
 * (and one per click of "Add media") - and an empty PDF slot is the same. On a SOCIAL post an empty
 * video slot is one too: "Remove video" empties the slot rather than taking it away, and the server
 * refuses to publish a post that holds a picture or video row with no file. (Off social a video
 * slot is saved as it always was: on a Video post that slot is the post.) All are dropped at the
 * persistence boundary, and an already-saved one is deleted.
 *
 * `social` is asked for, never defaulted: every place that walks the blocks a save sends has to
 * give the SAME answer, or the ids that come back are paired with the wrong blocks.
 */
export function persistable(block: EditorBlock, social: boolean): boolean {
  if (block.kind === ContentKind.IMAGE || block.kind === ContentKind.DOCUMENT) {
    return block.assetId != null;
  }
  if (block.kind === ContentKind.VIDEO && social) return block.assetId != null;
  return true;
}

/**
 * The DTO that updates a saved block. `assetId` verbatim - null must reach the server to CLEAR it -
 * and the text of every block that saves text, a PDF's title included: leaving it out means
 * "unchanged", which would drop every title edit on a saved post.
 */
export function toUpdateContentDto(block: EditorBlock, order: number): UpdateContentDto {
  return {
    text: savesText(block) ? block.text : undefined,
    assetId: block.assetId,
    order,
  };
}

/** The DTO used when a block is created (initial post contents, or an appended piece). */
export function toCreateContentDto(block: EditorBlock, order: number): CreateContentDto {
  return {
    kind: block.kind,
    text: savesText(block) ? block.text : undefined,
    assetId: block.assetId ?? undefined,
    order,
  };
}

/** An asset being attached to a gallery, from an upload or the Media Library. */
export interface AttachedAsset {
  id: string;
  blobUrl: string;
  mimeType: string;
  /** Set for a video; carried so its tile has a picture to show the moment it is attached. */
  posterUrl: string | null;
  /**
   * The small copy, carried for the same reason as `posterUrl`: the tile that appears the instant an
   * image is attached should be the 112px one, not the original downloaded to be scaled down.
   */
  thumbUrl?: string | null;
  /** The file's name and a PDF's page count, for a DOCUMENT's tile and its default title. */
  name?: string | null;
  pageCount?: number | null;
}

/**
 * The fields a DOCUMENT block takes from the PDF put into it - from an upload or the Media Library.
 *
 * Its title defaults to the file's name. A title the author TYPED is kept when the PDF is replaced;
 * one that was only ever the previous file's name follows the new file, or replacing "Q2 deck.pdf"
 * with "Q3 deck.pdf" would post Q3 under Q2's title.
 */
export function documentFields(block: EditorBlock, asset: AttachedAsset): Partial<EditorBlock> {
  const typed = block.text.trim();
  const keepTitle = typed !== '' && typed !== documentTitleFromFileName(block.assetName);
  return {
    assetId: asset.id,
    assetUrl: asset.blobUrl,
    assetMimeType: asset.mimeType,
    assetPosterUrl: null,
    assetThumbUrl: asset.thumbUrl ?? null,
    assetName: asset.name ?? null,
    assetPageCount: asset.pageCount ?? null,
    text: keepTitle ? block.text : documentTitleFromFileName(asset.name),
  };
}

/**
 * Put a PDF into the DOCUMENT block `key`, returning the next block list. By KEY and re-derived from
 * `prev`, like the gallery's insert, because the Media Library panel can sit open while the post
 * changes; a block removed meanwhile is a no-op, and anything but a PDF is never a document.
 */
export function attachDocument(
  prev: EditorBlock[],
  key: string,
  asset: AttachedAsset | undefined,
): EditorBlock[] {
  if (!asset || !isDocumentMime(asset.mimeType)) return prev;
  return prev.map((b) =>
    b.key === key && b.kind === ContentKind.DOCUMENT ? { ...b, ...documentFields(b, asset) } : b,
  );
}

/**
 * Append assets to the gallery containing `anchorKey`, returning the next block list.
 *
 * Takes a KEY rather than a captured group, and re-derives the run from `prev` at apply time, so a
 * caller that has been sitting open (the Media Library panel) can't act on a stale snapshot: images
 * added since land before these, the cap counts what is actually there, and a gallery deleted in
 * the meantime is a no-op rather than a resurrection. `imageLimit` is enforced HERE — the single
 * write point — because two callers (a drag-and-drop upload and a panel insert) can be in flight
 * at once and each would otherwise clamp against the same stale count and together overshoot.
 */
/**
 * What a post currently holds, in the terms the platform rules are written in.
 *
 * FIELDS, not items: a gallery of six images is ONE media field, and so is a single video. That
 * distinction is the whole of the "one text field and one media field" rule — it caps how many
 * sections a post has, and says nothing about how many images live inside one of them.
 *
 * An empty text block doesn't count. The composer starts every post with one, and a rule that
 * refused a second before the first was written would be unusable.
 */
export function compositionOf(blocks: EditorBlock[]): MediaComposition {
  return mediaCompositionOf(blocks);
}

/**
 * Move one block to sit immediately before another, addressing both by their stable key.
 *
 * Keys, not indices, because the rendered thumbnail list is NOT the block array: empty placeholders
 * are filtered out of the render, so the third tile is rarely the third block. Passing indices from
 * the DOM would move the wrong image whenever a placeholder is present — silently, since every tile
 * looks like a valid outcome.
 *
 * Order is the whole point of this: array position becomes `order: i` on save, which is the carousel
 * order at publish and decides which image is the cover.
 */
export function moveBlockBefore(
  prev: EditorBlock[],
  fromKey: string,
  beforeKey: string | null,
): EditorBlock[] {
  if (fromKey === beforeKey) return prev;
  const from = prev.findIndex((b) => b.key === fromKey);
  if (from === -1) return prev;
  const without = [...prev.slice(0, from), ...prev.slice(from + 1)];
  // `null` means "past the end of this run" — a drop on empty space after the last tile.
  if (beforeKey === null) {
    // Re-anchor to the end of the moved block's own contiguous run of the SAME kind, not the end of
    // the post: a gallery image dropped on empty space belongs after the last image, not after the
    // text block that follows it.
    let end = without.length;
    for (let i = from; i < without.length; i += 1) {
      if (without[i].kind !== prev[from].kind) {
        end = i;
        break;
      }
    }
    return [...without.slice(0, end), prev[from], ...without.slice(end)];
  }
  const to = without.findIndex((b) => b.key === beforeKey);
  if (to === -1) return prev;
  return [...without.slice(0, to), prev[from], ...without.slice(to)];
}

export function appendMediaToGallery(
  prev: EditorBlock[],
  anchorKey: string,
  assets: AttachedAsset[],
  imageLimit: number,
): EditorBlock[] {
  // A PDF is never a photo: everything not a video becomes an IMAGE block below, and an IMAGE that
  // is really a PDF fails the JPEG conversion at publish. PDFs go in through attachDocument.
  assets = assets.filter((a) => !isDocumentMime(a.mimeType));
  if (assets.length === 0) return prev;
  // Video still becomes its OWN block — it is a different Content kind, with a different body and
  // a different cap — but on a social post it is placed INSIDE the media run rather than after it,
  // so the composer shows one Media panel. The split here is about what a block IS; the grouping
  // is about how the panels read.
  const videos = assets.filter((a) => a.mimeType?.startsWith('video/'));
  assets = assets.filter((a) => !a.mimeType?.startsWith('video/'));
  if (assets.length === 0 && videos.length === 0) return prev;
  const anchorIndex = prev.findIndex((b) => b.key === anchorKey);
  if (anchorIndex === -1) return prev; // the gallery is gone — the user asked for it to go away

  // The run of consecutive IMAGE blocks containing the anchor.
  let start = anchorIndex;
  while (start > 0 && prev[start - 1].kind === ContentKind.IMAGE) start -= 1;
  let end = anchorIndex;
  while (end < prev.length - 1 && prev[end + 1].kind === ContentKind.IMAGE) end += 1;

  const capacity =
    imageLimit - prev.filter((b) => b.kind === ContentKind.IMAGE && b.assetId).length;
  const queue = capacity > 0 ? assets.slice(0, capacity) : [];

  // Fill the run's empty placeholders first (the one "Add images" creates), then append the rest
  // immediately after the run so they join this gallery in the order they were chosen.
  const next = prev.map((b, i) => {
    if (queue.length === 0 || i < start || i > end || b.kind !== ContentKind.IMAGE || b.assetId) {
      return b;
    }
    const a = queue.shift() as AttachedAsset;
    return {
      ...b,
      assetId: a.id,
      assetUrl: a.blobUrl,
      assetMimeType: a.mimeType,
      assetPosterUrl: a.posterUrl,
      assetThumbUrl: a.thumbUrl,
    };
  });
  // Images that didn't fit an existing placeholder, then any videos — both immediately after the
  // run, so everything lands in the order it was chosen.
  const appended: EditorBlock[] = [
    ...queue.map((a) => ({
      ...newImageBlock(),
      assetId: a.id,
      assetUrl: a.blobUrl,
      assetMimeType: a.mimeType,
      assetPosterUrl: a.posterUrl,
      assetThumbUrl: a.thumbUrl,
    })),
    ...videos.map((a) => ({
      ...newVideoBlock(),
      assetId: a.id,
      assetUrl: a.blobUrl,
      assetMimeType: a.mimeType,
      assetPosterUrl: a.posterUrl,
      assetThumbUrl: a.thumbUrl,
    })),
  ];
  if (appended.length > 0) next.splice(end + 1, 0, ...appended);
  return next;
}

/** One rendered block: a single piece, or a run of consecutive images shown as one gallery. */
export interface BlockGroup {
  key: string;
  kind: ContentKind;
  blocks: EditorBlock[];
}

/** IMAGE and VIDEO are the two kinds that share one panel on a social post. */
export const MEDIA_KINDS: ReadonlySet<ContentKind> = new Set([ContentKind.IMAGE, ContentKind.VIDEO]);

/**
 * Coalesce consecutive media into one group; everything else stays one block per group.
 *
 * On a SOCIAL post video joins the run, so the post has ONE "Media" panel rather than an Images
 * panel and a separate Video panel — a social post ships a single attachment set, and splitting it
 * across two panels asked the author to think about a distinction the destination does not make.
 * Elsewhere (Blog/Video/Podcast) a video is its own section of the document and keeps its own
 * block, which is what a video embedded between two paragraphs has to be.
 */
export function groupBlocks(blocks: EditorBlock[], mergeMedia: boolean): BlockGroup[] {
  const joins = (kind: ContentKind, lastKind: ContentKind) =>
    mergeMedia
      ? MEDIA_KINDS.has(kind) && MEDIA_KINDS.has(lastKind)
      : kind === ContentKind.IMAGE && lastKind === ContentKind.IMAGE;

  const out: BlockGroup[] = [];
  for (const block of blocks) {
    const last = out[out.length - 1];
    if (last && joins(block.kind, last.kind)) {
      last.blocks.push(block);
      // The group takes the kind of what it HOLDS: a run that gained an image is a gallery, and
      // the body renders each block by its own kind either way.
      if (block.kind === ContentKind.IMAGE) last.kind = ContentKind.IMAGE;
    } else {
      out.push({ key: block.key, kind: block.kind, blocks: [block] });
    }
  }
  return out;
}

/**
 * Why this post breaks the destination's one-text-box / one-media-panel rule — or would, with one
 * more box or panel added — or null when it doesn't. One function for the Add buttons and the
 * publish gate, so the two cannot drift into disagreeing about what a post may hold.
 *
 * ADDING counts every panel, empty ones included. The field count rightly skips an empty text box,
 * but asked whether ONE MORE was allowed it let a post with an empty caption grow a second box, then
 * a third. Every social post is one text box and one media panel, and an empty box is still the box.
 *
 * PUBLISHING counts what actually goes out. Delivery posts the FIRST text box, so an empty box only
 * matters when it comes before the caption — then the post would go out as its internal title. An
 * empty box after the caption, or an empty media tile, changes nothing that is delivered, and
 * refusing to publish over it would strand posts that are already approved and read-only.
 *
 * `social` covers a social post whose account isn't chosen yet: the rule is the same on every
 * social platform now, so there is nothing to wait for before applying it.
 */
export function panelRuleViolation(
  blocks: EditorBlock[],
  platform: SocialPlatform | null,
  social: boolean,
  adding?: 'text' | 'media',
): string | null {
  const composition = compositionOf(blocks);
  let textFields: number;
  let mediaFields: number;
  if (adding) {
    const groups = groupBlocks(blocks, social || platform != null);
    const textPanels = groups.filter((g) => g.kind === ContentKind.TEXT).length;
    // A PDF's panel is the post's media field as much as a gallery is.
    const mediaPanels = groups.filter(
      (g) => MEDIA_KINDS.has(g.kind) || g.kind === ContentKind.DOCUMENT,
    ).length;
    textFields = Math.max(composition.textFields, textPanels) + (adding === 'text' ? 1 : 0);
    mediaFields = Math.max(composition.mediaFields, mediaPanels) + (adding === 'media' ? 1 : 0);
  } else {
    const texts = blocks.filter((b) => b.kind === ContentKind.TEXT);
    const captionComesLater =
      texts.length > 1 && !texts[0].text.trim() && texts.some((b) => b.text.trim());
    textFields = composition.textFields + (captionComesLater ? 1 : 0);
    mediaFields = composition.mediaFields;
  }
  const byRules = mediaRuleViolation(platform, { ...composition, textFields, mediaFields });
  if (byRules) return byRules;
  // A PDF where no rule speaks for it: a Blog/Video/Podcast post (no platform at all), or a social
  // post with no account yet. Only LinkedIn publishes one; on a blog it would be silently dropped.
  if (!adding && composition.documents > 0 && !platform) {
    return social
      ? 'PDF carousels post to LinkedIn only. Choose a LinkedIn account for this post.'
      : 'PDF carousels post to LinkedIn only. Remove the PDF to publish this post.';
  }
  if (social && !platform) {
    if (textFields > 1) return 'Social posts take a single text field.';
    if (mediaFields > 1) return 'Social posts take a single media field.';
  }
  return null;
}

/** Which kinds of real media a post already holds — what an add has to be checked against. */
export interface MediaHeld {
  images: boolean;
  videos: boolean;
}

export function mediaHeld(blocks: EditorBlock[]): MediaHeld {
  return {
    images: blocks.some((b) => b.kind === ContentKind.IMAGE && b.assetId),
    videos: blocks.some((b) => b.kind === ContentKind.VIDEO && b.assetId),
  };
}

/**
 * Why a PDF slot can't be added to this post, or null when it can. A PDF is the whole attachment of
 * a LinkedIn post, so it can't join photos or video - and a post takes one. Only media the post
 * actually HOLDS counts: an empty "Add media" tile is removed when the PDF slot is added
 * (withoutEmptyMedia), so it must not be the reason the button is dead.
 */
export function addDocumentBlocked(blocks: EditorBlock[]): string | null {
  const slot = blocks.find((b) => b.kind === ContentKind.DOCUMENT);
  if (slot) return slot.assetId ? 'This post already has a PDF.' : 'Add the PDF in the PDF carousel panel.';
  if (blocks.some((b) => MEDIA_KINDS.has(b.kind) && b.assetId)) return DOCUMENT_EXCLUSIVE_MESSAGE;
  return null;
}

/** The blocks without empty photo/video tiles - what adding a PDF slot replaces them with. */
export function withoutEmptyMedia(blocks: EditorBlock[]): EditorBlock[] {
  return blocks.filter((b) => !(MEDIA_KINDS.has(b.kind) && !b.assetId));
}

/** The blocks without an empty PDF slot - what adding photos or video replaces it with. */
export function withoutEmptyDocuments(blocks: EditorBlock[]): EditorBlock[] {
  return blocks.filter((b) => !(b.kind === ContentKind.DOCUMENT && !b.assetId));
}

/**
 * Whether adding `assets` to a post that holds `held` would put photos and video in one SOCIAL post,
 * which no destination accepts (MEDIA_MIXING_MESSAGE). Counts what the post already has, so a video
 * picked for a post of photos is caught, not just a mixed selection.
 */
export function wouldMixMedia(held: MediaHeld, assets: readonly { mimeType: string }[]): boolean {
  if (assets.length === 0) return false;
  const isVideo = (a: { mimeType: string }) => a.mimeType.startsWith('video/');
  return (held.videos || assets.some(isVideo)) && (held.images || assets.some((a) => !isVideo(a)));
}
